import dayjs from 'dayjs';
import {
  type DerogationWithPolicyAndCustomerEntity,
  type PolicyBranchesForBrokerEntity,
} from 'korint-sdk';
import type {
  CustomerDisplay,
  KorintConfig,
  KorintModulesConfig,
  KorintModulesProductsConfig,
} from 'korint-sdk/react';
import { AWAITING_FIRST_PAYMENT, STOP_SCHEDULED } from 'korint-sdk/react';
import { config } from '../../config';
import { defaultErrorMessage, errorDictionary } from './configs/errors';

import { uniqAppConfig } from './configs/uniq.config';
import {
  tutassurAssistanceConfig,
  tutassurHiscoxMultirisquesProConfig,
  tutassurHiscoxResponsabiliteCivileProConfig,
  tutassurMilaMultirisquesProConfig,
  tutassurMilaResponsabiliteCivileProConfig,
  tutassurProtectionJuridiqueConfig,
  tutassurTransportMarchandisesEPriveConfig,
  tutassurTransportMarchandisesGeneraliConfig,
  tutassurTransportMarchandisesHelvetiaConfig,
  tutassurMilaMultirisqueHabitationLocataireConfig,
  tutassurMilaProprietaireNonOccupantConfig,
  tutassurProtectionJuridiqueProConfig,
  tutassurHiscoxResponsabiliteDirigeantsConfig,
  tutassurHiscoxCyberConfig,
} from './products';
import { customerDisplayName } from '@folio/utils/customerDisplayName';

const productsConfig = {
  tutassurAssistance: tutassurAssistanceConfig,
  tutassurProtectionJuridique: tutassurProtectionJuridiqueConfig,
  tutassurMilaMultirisquesPro: tutassurMilaMultirisquesProConfig,
  tutassurMilaResponsabiliteCivilePro: tutassurMilaResponsabiliteCivileProConfig,
  tutassurHiscoxMultirisquesPro: tutassurHiscoxMultirisquesProConfig,
  tutassurHiscoxResponsabiliteCivilePro: tutassurHiscoxResponsabiliteCivileProConfig,
  tutassurTransportMarchandisesGenerali: tutassurTransportMarchandisesGeneraliConfig,
  tutassurTransportMarchandisesHelvetia: tutassurTransportMarchandisesHelvetiaConfig,
  tutassurTransportMarchandisesEPrive: tutassurTransportMarchandisesEPriveConfig,
  tutassurMilaMultirisqueHabitationLocataire: tutassurMilaMultirisqueHabitationLocataireConfig,
  tutassurMilaProprietaireNonOccupant: tutassurMilaProprietaireNonOccupantConfig,
  tutassurProtectionJuridiquePro: tutassurProtectionJuridiqueProConfig,
  tutassurHiscoxResponsabiliteDirigeants: tutassurHiscoxResponsabiliteDirigeantsConfig,
  tutassurHiscoxCyber: tutassurHiscoxCyberConfig,
} satisfies Record<string, KorintModulesProductsConfig>;

const korintModulesConfig = {
  policyholderContactsModuleConfig: {
    contacts: [
      {
        type: 'email',
        showIcon: true,
        value: 'gestion@tutassur.fr',
      },
      {
        type: 'phone',
        showIcon: true,
        value: '04 65 84 25 34',
      },
    ],
  },
  intermediaryUserManagement: {
    showTeamManagement: [],
  },
  intermediaryUserCreation: {
    inviteRole: {
      RETAIL: ['RETAIL'],
      WHOLESALE: ['WHOLESALE'],
    },
  },
  intermediaryGeneralModule: {
    displayInscriptionLink: true,
  },
  intermediaryDetailsLayout: {
    buildBrokerTitle: ({ companyInfo, customFields }) =>
      companyInfo?.name ||
      [customFields.brokerDirectorFirstName, customFields.brokerDirectorLastName]
        .filter(Boolean)
        .join(' ') ||
      undefined,
  },
  derogationValidations: {
    filtersConfig: {
      derogationStatus: {
        type: 'select' as const,
        options: ['CREATED', 'APPROVED', 'DENIED'],
      },
    },
    baseFilters: {},
    columns: [
      {
        name: 'customerName',
        type: 'string',
        accessor: (entity: DerogationWithPolicyAndCustomerEntity) =>
          customerDisplayName(
            entity.customers,
            productsConfig[entity.policy.productId as keyof typeof productsConfig]
              ?.customerInformations?.fieldsConfig,
          ),
      },
      {
        name: 'companyName',
        type: 'string',
        accessor: ({ brokerageFirm }: DerogationWithPolicyAndCustomerEntity) =>
          brokerageFirm?.companyInfo?.name,
      },
      {
        name: 'derogationAction',
        type: 'status',
        translationNamespace: 'derogationAction',
        accessor: ({ branch }: DerogationWithPolicyAndCustomerEntity) =>
          branch?.derogations?.find(branchDerogation => branchDerogation.status === 'DENIED')
            ?.action ??
          branch?.derogations?.find(branchDerogation => branchDerogation.status === 'CREATED')
            ?.action ??
          branch?.derogations?.find(branchDerogation => branchDerogation.status === 'APPROVED')
            ?.action ??
          'SIGN',
      },
      {
        name: 'status',
        type: 'status',
        translationNamespace: 'derogationStatus',
        accessor: ({ branch }: DerogationWithPolicyAndCustomerEntity) =>
          branch?.derogations?.find(branchDerogation => branchDerogation.status === 'DENIED')
            ?.status ??
          branch?.derogations?.find(branchDerogation => branchDerogation.status === 'CREATED')
            ?.status ??
          branch?.derogations?.find(branchDerogation => branchDerogation.status === 'APPROVED')
            ?.status,
      },
      {
        name: 'allClientDocumentsOk',
        type: 'boolean',
        accessor: ({ policy }) =>
          policy.customFields?.find(field => field.key === 'allClientDocumentsOk')?.value as
            | boolean
            | undefined,
      },
      {
        name: 'createdAt',
        type: 'date',
        format: 'datetime',
        accessor: ({ branch }) =>
          branch?.derogations?.find(branchDerogation => branchDerogation.status === 'DENIED')
            ?.createdAt ??
          branch?.derogations?.find(branchDerogation => branchDerogation.status === 'CREATED')
            ?.createdAt ??
          branch?.derogations?.find(branchDerogation => branchDerogation.status === 'APPROVED')
            ?.createdAt,
      },
    ],
  },
  methodPayment: {
    stripePublishableKey: config.STRIPE_PUBLISHABLE_KEY,
  },
  portfolio: {
    filtersConfig: {
      policyStatus: {
        type: 'select',
        options: [
          'POLICY_SIGNED',
          'POLICY_STARTED',
          'POLICY_STOPPED',
          'POLICY_SUSPENDED',
          STOP_SCHEDULED,
          AWAITING_FIRST_PAYMENT,
        ],
      },
      endDate: {
        type: 'date',
      },
      creationDate: {
        type: 'date',
      },
      complianceAlerts: {
        type: 'checkbox',
      },
      brokerageFirmName: {
        type: 'textInput',
      },
      assignedBroker: {
        type: 'assignedBrokerSelect',
      },
      productIds: {
        type: 'select',
        options: Object.keys(productsConfig),
      },
    },
    baseFilters: { viewType: 'live' },
    expandCustomers: true,
    expandBrokerageFirm: true,
    columns: [
      {
        name: 'name',
        type: 'string',
        accessor: ({ customers, policy }: PolicyBranchesForBrokerEntity) =>
          customerDisplayName(
            customers ?? [],
            productsConfig[policy.productId as keyof typeof productsConfig]?.customerInformations
              ?.fieldsConfig,
          ),
      },
      {
        name: 'author',
        type: 'string',
        accessor: ({ policy }: PolicyBranchesForBrokerEntity) =>
          policy.author?.email ||
          (policy.author?.firstName &&
            policy.author?.lastName &&
            `${policy.author?.firstName} ${policy.author?.lastName}`),
      },
      {
        name: 'brokerageFirm',
        type: 'string',
        accessor: ({ brokerageFirm }: PolicyBranchesForBrokerEntity) =>
          brokerageFirm?.companyInfo?.name,
      },
      {
        name: 'createdAt',
        type: 'date',
        format: 'datetime',
        path: 'policy.createdAt',
      },
      {
        name: 'productId',
        type: 'product',
        path: 'policy.productId',
      },
      {
        name: 'status',
        type: 'status',
        path: 'policy.status',
      },
      {
        name: 'externalId',
        type: 'string',
        path: 'policy.externalId',
      },
      {
        name: 'startedAt',
        type: 'date',
        format: 'date',
        path: 'policy.startedAt',
      },
      {
        name: 'renewalDate',
        type: 'date',
        format: 'date',
        path: 'policy.endedAt',
      },
    ],
  },
  policyLifecycleSummaryModuleConfig: {
    dateDisplayFormat: 'DD/MM/YYYY HH:mm',
  },
  ongoingNewBusiness: {
    filtersConfig: {
      policyStatus: {
        type: 'select',
        options: ['POLICY_CREATED', 'POLICY_QUOTED', 'POLICY_CONFIRMED'],
      },
      brokerageFirmName: {
        type: 'textInput',
      },
      assignedBroker: {
        type: 'assignedBrokerSelect',
      },
      productIds: {
        type: 'select',
        options: Object.keys(productsConfig),
      },
      creationDate: {
        type: 'date',
      },
    },
    baseFilters: {
      viewType: 'newBusiness',
    },
    initialFilters: [
      {
        field: 'creationDate',
        value: {
          start: dayjs().subtract(30, 'day').format('YYYY-MM-DD'),
        },
      },
    ],
    expandCustomers: true,
    expandBrokerageFirm: true,
    columns: [
      {
        name: 'name',
        type: 'string',
        accessor: ({ customers, policy }: PolicyBranchesForBrokerEntity) =>
          customerDisplayName(
            customers ?? [],
            productsConfig[policy.productId as keyof typeof productsConfig]?.customerInformations
              ?.fieldsConfig,
          ),
      },
      {
        name: 'author',
        type: 'string',
        accessor: ({ policy }: PolicyBranchesForBrokerEntity) =>
          policy.author?.email ||
          (policy.author?.firstName &&
            policy.author?.lastName &&
            `${policy.author?.firstName} ${policy.author?.lastName}`),
      },
      {
        name: 'brokerageFirm',
        type: 'string',
        accessor: ({ brokerageFirm }: PolicyBranchesForBrokerEntity) =>
          brokerageFirm?.companyInfo?.name,
      },
      {
        name: 'createdAt',
        type: 'date',
        format: 'datetime',
        path: 'branch.createdAt',
      },
      {
        name: 'productId',
        type: 'product',
        path: 'policy.productId',
      },
      {
        name: 'status',
        type: 'status',
        path: 'policy.status',
      },
    ],
  },
  accountingDashboard: {
    filtersConfig: {
      policyStatus: {
        type: 'select',
        options: ['POLICY_SIGNED', 'POLICY_STARTED', 'POLICY_STOPPED', 'POLICY_SUSPENDED'],
      },
      terminationReason: {
        type: 'select',
        options: [
          'AMICABLE',
          'CLAIMS',
          'CONTRACT_REPLACEMENT',
          'DEATH',
          'DESTRUCTION',
          'FALSE_DECLARATION',
          'MAIN_DUE_DATE',
          'NON_PAYMENT',
          'REFUSAL_TO_INCREASE_PREMIUM',
          'SALE',
          'VOID',
          'WAIVER',
          'OTHER',
        ],
      },
    },
  },
  complianceModuleConfig: {
    enableManualAlert: false,
  },
  businessRolesConfig: {
    RETAIL: {
      redirectRules: {
        hasUserProducts: '/portfolio',
        hasPendingDistributionRequest: '/waiting',
        noDistributionRequest: (brokerageFirmId: string) =>
          `brokerage_firm_9hYLyYKM3GTVqMECjg3NH/brokerage-firm/${brokerageFirmId}/information`,
        default: () => '/nobody',
      },
    },
    WHOLESALE: {
      redirectRules: {
        default: () => '/brokerage-firm-list',
      },
    },
    WHOLESALE_NO_EMAIL: {
      redirectRules: {
        default: () => '/brokerage-firm-list',
      },
    },
    DRIVER: {
      redirectRules: {
        default: policyId => `/policyholder/${policyId}`,
      },
    },
    SUPER_ADMIN: {
      redirectRules: {
        default: () => '/admin',
      },
    },
  },
  distributionRequestListModule: {
    intermediaryAdditionalInformationFields: [
      {
        name: 'minCommission',
        type: 'number',
        required: true,
        DEPRECATED_placeholder: '10',
        apiDataAccessor: 'minCommission',
      },
      {
        name: 'maxCommission',
        type: 'number',
        required: true,
        DEPRECATED_placeholder: '15',
        apiDataAccessor: 'maxCommission',
      },
      {
        name: 'maxManagementFee',
        type: 'number',
        required: true,
        DEPRECATED_placeholder: '200',
        apiDataAccessor: 'maxManagementFee',
      },
      {
        name: 'minTotalCommission',
        type: 'number',
        required: false,
        apiDataAccessor: 'minTotalCommission',
        hidden: true,
      },
      {
        name: 'maxTotalCommission',
        type: 'number',
        required: false,
        apiDataAccessor: 'maxTotalCommission',
        hidden: true,
      },
    ],
  },
  documents: {
    uploadModes: {
      policyholderDocuments: 'batch',
      nonDelegatedDocuments: 'category',
      brokerageFirmDocuments: 'batch',
    },
  },
  aiClassification: {
    interfaces: {
      policyholderDashboard: {
        enabled: true,
        showQualityFlags: true,
        qualityFlagsToShow: [
          'corrupted_document',
          'multiple_pages',
          'poor_image_quality',
          'document_oriented_incorrectly',
          'partially_visible',
          'language_mismatch',
          'handwritten_sections',
        ],
        showReclassificationEvents: false,
        showJudgeVerdicts: false,
      },
      brokerageFirmDocuments: {
        enabled: true,
        showQualityFlags: true,
        qualityFlagsToShow: [
          'corrupted_document',
          'multiple_pages',
          'poor_image_quality',
          'document_oriented_incorrectly',
          'partially_visible',
          'language_mismatch',
          'handwritten_sections',
        ],
        showReclassificationEvents: false,
        showJudgeVerdicts: false,
      },
    },
  },
} as const satisfies KorintModulesConfig;

const korintConfig = {
  uniqAppConfig: uniqAppConfig,
  modulesConfig: korintModulesConfig,
  dataAccessors: {
    customerDisplayName: (customer: CustomerDisplay) => {
      if (!customer) return null;
      if (customer.companyName) return customer.companyName;
      return [customer.firstName, customer.lastName].filter(value => value).join(' ');
    },
  },
  productsConfig,
  errorsConfig: {
    defaultErrorMessage: defaultErrorMessage,
    errorDictionary: errorDictionary,
  },
  supportLinkRoles: ['WHOLESALE', 'WHOLESALE_NO_EMAIL'],
} as const satisfies KorintConfig;

export default korintConfig;
